/**
 * React Query hooks for the unified Creators tab — paginated list +
 * tab-aggregated KPI. Pair: one list query + one KPI query, both keyed
 * off the campaign id so SSE / mutation invalidation targets one tab
 * cleanly.
 *
 * Why two separate hooks rather than a combined one:
 * 1. Pagination of the list shouldn't refetch the KPI (and vice versa).
 * 2. SSE-driven unread-count patches mutate the list cache in place via
 *    queryClient.setQueriesData (rosterCacheOps.patchRosterEntry) — that
 *    pattern is straightforward when the cache holds just the list page,
 *    not a wrapped { list, kpi } shape.
 * 3. The KPI endpoint is cheaper than the list (single $group) so it
 *    keeps a shorter staleTime without dragging the list along.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  CreatorsTabKPI,
  RosterEntryCreators,
  RosterEntryRejected,
} from '@discovr/types';

import {
  createCampaignDetailApi,
  type UnifiedListParams,
} from '../api/campaignQueries';
import {
  createRosterMutationsApi,
  type BulkApproveResult,
} from '../api/rosterMutations';
import type { PagedResult } from '../api/shared';
import { campaignKeys } from '../queryKeys';

const api = createCampaignDetailApi();
const rosterApi = createRosterMutationsApi();

/**
 * Paginated Creators-tab list. Pass the same `params` shape every render
 * (status/page/limit) so the queryKey stabilises and React Query caches
 * cleanly. Default page=1, limit=20 — matches the BE default.
 */
export function useCreatorsTab(
  campaignId: string,
  params: UnifiedListParams = {},
) {
  return useQuery<PagedResult<RosterEntryCreators>>({
    queryKey: campaignKeys.rosterTab(campaignId, 'creators', params),
    queryFn: () => api.listCreators(campaignId, params),
    enabled: !!campaignId,
    staleTime: 30_000,
    // SSE keeps this list live; disabling focus refetch keeps `isRefetching` a
    // clean "SSE-driven update" signal for the JumpToTopPill.
    refetchOnWindowFocus: false,
  });
}

/**
 * Creators-tab forecast — tab-aggregated estimated_reach +
 * estimated_budget + creator_count. Scope is fixed per tab (no
 * ?status= mirroring), so the key never carries sub-filter params.
 *
 * `options.enabled` lets a caller suppress the fetch outright (e.g. the
 * public read-only share view, which has no authed session to fetch a
 * campaign-wide aggregate with) — same `enabled: !!campaignId &&
 * (options?.enabled ?? true)` precedent as useLastImportResult. Defaults to
 * enabled so every existing caller is unchanged.
 */
export function useCreatorsKPI(campaignId: string, options?: { enabled?: boolean }) {
  return useQuery<CreatorsTabKPI>({
    queryKey: campaignKeys.rosterKpisCreators(campaignId),
    queryFn: () => api.getCreatorsKPI(campaignId),
    enabled: !!campaignId && (options?.enabled ?? true),
    staleTime: 30_000,
  });
}

/**
 * Paginated Rejected-tab list. Terminal phase — read-only. Shares the
 * same staleTime + params shape as useCreatorsTab so the two queries
 * compose cleanly under the Creators-tab sub-tab switcher.
 */
export function useRejectedTab(
  campaignId: string,
  params: UnifiedListParams = {},
) {
  return useQuery<PagedResult<RosterEntryRejected>>({
    queryKey: campaignKeys.rosterTab(campaignId, 'rejected', params),
    queryFn: () => api.listRejected(campaignId, params),
    enabled: !!campaignId,
    staleTime: 30_000,
    // See useCreatorsTab: SSE-live; focus refetch off → clean pill signal.
    refetchOnWindowFocus: false,
  });
}

/**
 * Bulk "approve all eligible" creators on a campaign (one-click). The route is
 * role-dispatched by the BE (brand → pending creators with a main selected;
 * admin → brand-accepted creators). On success every roster list + count + the
 * tab KPIs are invalidated so the affected sub-tabs refresh.
 */
export function useApproveAllCreators(campaignId: string) {
  const queryClient = useQueryClient();
  return useMutation<BulkApproveResult, Error, void>({
    mutationFn: () => rosterApi.approveAllCreators(campaignId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
      void queryClient.invalidateQueries({ queryKey: ['roster-kpis', campaignId] });
      void queryClient.invalidateQueries({ queryKey: campaignKeys.generalReports(campaignId) });
    },
  });
}
